import Axios from "../../../utils/axiosInterceptor";
import { PATIENT_APIS } from "../../../utils/constants";
import { errorMessage } from "../../../utils/notificationUtils";
import {
  buildHeritageCasePreAuthOthersRequest,
  type HeritagePreAuthFormValues,
} from "./heritageCasePreAuthOthersMapper";

export type { HeritagePreAuthFormValues } from "./heritageCasePreAuthOthersMapper";
export {
  buildHeritageCasePreAuthOthersRequest,
  mapHeritagePreAuthOthersToForm,
} from "./heritageCasePreAuthOthersMapper";

export const HERITAGE_PAYER_NAME = "Heritage";

const HERITAGE_API_BASE = PATIENT_APIS.HERITAGE_SERVICE;

const dateFormat = "DD-MM-YYYY HH:mm:ss";

export type HeritagePatientFetchDetails = {
  identityNumber: string;
  uhidNo?: string;
  hospitalId: number;
  schemeId: number;
  cardCategory?: string;
};

export const HERITAGE_ADMISSION_TYPES = [
  "Day Care",
  "Emergency",
  "Maternity",
  "OPD",
  "Planned",
] as const;

export const HERITAGE_ROOM_CATEGORIES = [
  "General Bed",
  "Double Sharing",
  "I.C.U",
  "Semi Private",
  "Private",
  "I.C.C.U",
  "Day Care",
] as const;

/** Default member/admission values from case & visit (before portal pre-auth exists). */
export const getHeritagePrefillFromBasicDetails = (basicDetails: any) => {
  const patient = basicDetails?.patientResponse;
  const cases = basicDetails?.casesResponse;
  const visit = basicDetails?.visitInfoResponse;
  const scheme =
    patient?.patientSchemeNewResponseList?.[0] ||
    basicDetails?.patientSchemeNewResponseList?.[0];

  return {
    patientName:
      patient?.fullName || patient?.name || cases?.patientName || "",
    policyNumber: patient?.patientSchemeNewResponseList?.[0]?.policyNumber || patient?.policyNumber || scheme?.policyNumber || "",
    cardNumber:
      patient?.identityNumber ||
      scheme?.identityNumber ||
      cases?.memberId?.toString() ||
      "",
    empId: patient?.employeeId || scheme?.employeeId || "",
    patientMobile: patient?.primaryMobile || "",
    mrn: patient?.uhidNo || visit?.opNumber?.toString() || "",
    admissionDate: visit?.doa || cases?.doa || null,
    dischargeDate: visit?.dod || cases?.dod || null,
    treatingDoctorName: cases?.doctorName || "",
    diagnosisAilment: "",
    admissionType: undefined,
  };
};

export type BuildHeritagePreAuthPayloadParams = {
  schemeMetaDataId: number | string | undefined;
  portalMetaDataId?: number;
  casePreAuthId?: number;
  hospitalId?: number;
  schemeId?: number;
  beneficiaryId?: string;
  patientOpdIpdNumber?: string | number | undefined;
  integratedWithPortal?: boolean;
  action?: string;
  formValues: HeritagePreAuthFormValues;
};

export type HeritageApprovePayload = {
  caseId: number;
  visitId: number;
  stage: string;
  approved: boolean;
  casePreAuthId: number;
  portalMetaDataId?: number;
  remarks?: string;
  preAuthRequest: ReturnType<typeof buildHeritagePreAuthPayload>;
};

const resolvePayerData = (response: any) => {
  const payers =
    response?.portals?.["heritage"]?.payers ||
    response?.portals?.Heritage?.payers ||
    response?.portals?.ihx?.payers ||
    {};
  const matchedPayerKey = Object.keys(payers).find((payerKey) =>
    payerKey?.toUpperCase()?.includes(HERITAGE_PAYER_NAME.toUpperCase())
  );

  return matchedPayerKey ? payers?.[matchedPayerKey] : null;
};

const resolvePayerProviderData = (response: any) => {
  const payerProviders =
    response?.portals?.["heritage"]?.payerProviders ||
    response?.portals?.Heritage?.payerProviders ||
    response?.portals?.ihx?.payerProviders ||
    {};
  const matchedPayerKey = Object.keys(payerProviders).find((payerKey) =>
    payerKey?.toUpperCase()?.includes(HERITAGE_PAYER_NAME.toUpperCase())
  );

  return matchedPayerKey ? payerProviders?.[matchedPayerKey] : null;
};

/** Maps Heritage pre-auth API response for redux portalData (Ayushman-style). */
export const mapHeritagePortalData = (response: any) => {
  if (!response || typeof response !== "object") {
    return null;
  }
  return {
    casePreAuthOthersResponse: response,
    casePreAuthHeritage: response,
  };
};

/**
 * Builds pre-auth request body aligned with HeritageCasePreAuthRequestNew (same shape as CGHS/Ayushman others flow).
 */
export const buildHeritagePreAuthPayload = ({
  schemeMetaDataId,
  portalMetaDataId,
  casePreAuthId = 0,
  hospitalId,
  schemeId,
  beneficiaryId,
  patientOpdIpdNumber,
  integratedWithPortal = true,
  action = "save",
  formValues,
}: BuildHeritagePreAuthPayloadParams) => {
  const casePreAuthOthersRequest = buildHeritageCasePreAuthOthersRequest(formValues);

  return {
    schemeMetaDataId: String(schemeMetaDataId),
    portalMetaDataId,
    casePreAuthId,
    casePreAuthRequest: {
      beneficiaryId,
      patientOpdIpdNumber,
    },
    casePreAuthOthersRequest,
    casePreAuthIhxRequest: null,
    casePreAuthCghsRequest: null,
    casePreAuthVidalRequest: null,
    casePreAuthStarhealthRequest: null,
    casePreAuthEchsRequest: null,
    hospitalId,
    schemeId,
    totalAmount:
      formValues?.estimatedCost != null
        ? String(formValues.estimatedCost)
        : formValues?.claimedAmount != null
          ? String(formValues.claimedAmount)
          : "",
    integratedWithPortal,
    action,
    doa: formValues?.admissionDate || null,
    dod: formValues?.dischargeDate || null,
  };
};

export const buildHeritageApprovePayload = (
  params: Omit<HeritageApprovePayload, "preAuthRequest"> & {
    preAuthRequest: ReturnType<typeof buildHeritagePreAuthPayload>;
  }
): HeritageApprovePayload => ({
  caseId: params.caseId,
  visitId: params.visitId,
  stage: params.stage,
  approved: params.approved,
  casePreAuthId: params.casePreAuthId,
  portalMetaDataId: params.portalMetaDataId,
  remarks: params.remarks,
  preAuthRequest: params.preAuthRequest,
});

// ---------------------------------------------------------------------------
// Registration stage
// ---------------------------------------------------------------------------

export const fetchHeritageBeneficiaryDetails = async (
  data: HeritagePatientFetchDetails
) => {
  try {
    const response = await Axios.post(`${HERITAGE_API_BASE}/fetch-details`, data);
    return response.data;
  } catch (error: any) {
    console.error("Failed to fetch Heritage beneficiary details:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to fetch Heritage beneficiary details"
    );
  }
};

// ---------------------------------------------------------------------------
// PreAuth stage
// ---------------------------------------------------------------------------

export const getAllCasePreAuthByCaseIdAndVisitId = async (
  caseId: number,
  visitId: number
) => {
  try {
    const response = await Axios.get(
      `${HERITAGE_API_BASE}/cases/${caseId}/visitInfo/${visitId}/getAllCasePreAuthByCaseIdAndVisitId`
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to get Heritage pre-auth:", error?.response?.data);
    errorMessage(error?.response?.data?.data?.message || "Failed to get Heritage pre-auth data. Please try again.");
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to get Heritage pre-auth data"
    );
  }
};

/** Alias used by preAuth slice */
export const fetchPortalDataHeritage = getAllCasePreAuthByCaseIdAndVisitId;

export const saveDraftHeritage = async (
  caseId: number,
  visitId: number,
  formData: any
) => {
  try {
    const response = await Axios.post(
      `${HERITAGE_API_BASE}/cases/${caseId}/visitInfo/${visitId}/casePreAuth`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot save Heritage pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot save Heritage pre-auth details"
    );
  }
};

export const updateDraftHeritage = async (
  caseId: number,
  visitId: number,
  casePreAuthId: number,
  formData: any
) => {
  try {
    const response = await Axios.patch(
      `${HERITAGE_API_BASE}/cases/${caseId}/visitInfo/${visitId}/casePreAuthId/${casePreAuthId}`,
      formData
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update Heritage pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot update Heritage pre-auth details"
    );
  }
};

export const intimateHeritagePreAuth = async (
  caseId: number,
  visitId: number,
  formData: any
) => {
  return saveDraftHeritage(caseId, visitId, {
    ...formData,
    action: "intimate",
  });
};

/** Initiate pre-auth on Heritage portal (saves draft when casePreAuthId is 0). */
export const initiateCasePreAuthHeritage = async (
  caseId: number,
  visitId: number,
  payload: any
) => {
  try {
    const response = await Axios.post(
      `${HERITAGE_API_BASE}/cases/${caseId}/visitInfo/${visitId}/initiateCasePreAuth`,
      payload
    );
    return response.data;
  } catch (error: any) {
    console.error("Failed to initiate Heritage pre-auth on portal:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data?.data?.message ||
        "Failed to initiate Heritage pre-auth on portal"
    );
  }
};

export const approveHeritagePreAuth = async (formData: HeritageApprovePayload) => {
  try {
    const response = await Axios.post(`${HERITAGE_API_BASE}/approve`, formData);
    return response.data;
  } catch (error: any) {
    console.error("Failed to approve Heritage pre-auth:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Failed to approve Heritage pre-auth"
    );
  }
};

/** @deprecated use saveDraftHeritage */
export const postHeritagePreAuth = saveDraftHeritage;

/** @deprecated use updateDraftHeritage */
export const updateHeritagePreAuth = updateDraftHeritage;

/** @deprecated use getAllCasePreAuthByCaseIdAndVisitId */
export const getHeritagePreAuth = getAllCasePreAuthByCaseIdAndVisitId;

// ---------------------------------------------------------------------------
// PreAuth stage – portal metadata helpers (shared IHX/CGHS adapter APIs)
// ---------------------------------------------------------------------------

export const getHeritageDiagnosisOptions = async (
  portalMetaDataId: number,
  hospitalId: number,
  schemeId: number
) => {
  const response = await Axios.post(
    `${PATIENT_APIS.GET_DIAGNOSIS_DATA}/portalMetaDataId/${portalMetaDataId}/hospitalId/${hospitalId}/schemeId/${schemeId}`,
    { procedureCooperateMappingId: null }
  );

  const payerData = resolvePayerData(response.data);
  const diagnosisList = payerData?.diagnosisList || [];
  return diagnosisList.map((item: any) => ({
    value: item.id,
    label: item.diagnosis,
  }));
};

export const getHeritageRoomTypeOptions = async (
  hospitalId: number,
  portalMetaDataId: number,
  schemeId: number
) => {
  const response = await Axios.get(
    `${PATIENT_APIS.GET_ROOMTYPES_DATA}/portalMetaDataId/${portalMetaDataId}/schemeId/${schemeId}/hospitalId/${hospitalId}`
  );

  const payerData = resolvePayerData(response.data);
  const roomTypes = payerData?.roomTypes || [];
  const generalRoomTypes = payerData?.generalRoomTypes || [];

  return {
    roomTypes: roomTypes.map((item: any) => ({
      value: item.id,
      label: item.providerRoomType,
    })),
    generalRoomTypes: generalRoomTypes.map((item: any) => ({
      value: item.id,
      label: item.roomType,
    })),
  };
};

export const getHeritageProcedureOptions = async (
  hospitalId: number,
  portalMetaDataId: number,
  schemeId: number
) => {
  const response = await Axios.get(
    `${PATIENT_APIS.GET_PROCEDURE_DATA}/portalMetaDataId/${portalMetaDataId}/hospitalId/${hospitalId}/schemeId/${schemeId}`
  );

  const payerData = resolvePayerData(response.data);
  const procedureList = payerData?.procedureList || [];
  return procedureList.map((item: any) => ({
    value: item.id,
    label: item.procedure,
  }));
};

export const getHeritageTreatmentOptions = async (portalMetaDataId: number) => {
  const response = await Axios.get(
    `${PATIENT_APIS.GET_TREATMENT_DATA}/portalMetaDataId/${portalMetaDataId}`
  );

  const payerProviderData = resolvePayerProviderData(response.data);
  return payerProviderData?.lineOfTreatments || [];
};

export const claimValidationHeritage = async (data: any) => {
  try {
    const response = await Axios.post(`${HERITAGE_API_BASE}/claim/validate`, data);
    return response.data;
  } catch (error: any) {
    console.error("Cannot validate Heritage claim details:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot validate Heritage claim details"
    );
  }
};

export const updateClaimValidationHeritage = async (
  caseId: number,
  visitId: number,
  data: any
) => {
  try {
    const response = await Axios.patch(
      `${HERITAGE_API_BASE}/claim/caseId/${caseId}/visitId/${visitId}/update`,
      data
    );
    return response.data;
  } catch (error: any) {
    console.error("Cannot update Heritage claim validation details:", error?.response?.data);
    throw new Error(
      error?.response?.data?.message ||
        error?.response?.data ||
        "Cannot update Heritage claim validation details"
    );
  }
};

export { dateFormat as HERITAGE_DATE_FORMAT };
